import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DestinazioneContext } from "../context/DestinazioneContext";
import { LoginContext } from "../context/LoginContext";
import { ThemeContext } from "../context/ThemeContext";
import { Container, Form, Button, Alert, Spinner, Modal, Collapse } from "react-bootstrap";
import configService from "../services/configService";
import orderService from "../services/orderService";
import clientiService from "../services/clientiService";
import { getClientConfig } from "../services/clientConfigCache";
import { parseItalianDate, isValidItalianDate } from '../utils/convertDate';
import { RELEASE_HISTORY, getCurrentRelease } from '../constants/releaseHistory';
import {
  forgetRememberedCredentials,
  loadRememberedCredentials,
  persistRememberedCredentials,
} from "../utils/rememberedCredentials";

const formatDeployDateIt = (ymd) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || '—';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

const PANIFY_CLOUD_FALLBACK_LOGO = `${process.env.PUBLIC_URL}/images/logo/panify-cloud-login.svg`;

/** Logo azienda da API → data URL, o null se assente / non valido */
const normalizeCompanyLogoDataUrl = (raw) => {
  if (raw == null) return null;
  const s =
    typeof raw === 'string'
      ? raw.trim()
      : String(raw?.data ?? raw?.logo ?? raw?.logodit ?? '').trim();
  if (!s || s === 'null' || s === 'undefined') return null;
  if (s.length < 80) return null;
  if (s.startsWith('data:image/')) return s;
  return `data:image/jpeg;base64,${s}`;
};

/**
 * True se l'immagine è quasi tutta bianca / trasparente (logo “vuoto” su sfondo chiaro).
 */
const isMostlyBlankOrWhiteLogo = (img) => {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h || w < 12 || h < 12) return true;

  const sampleSize = 64;
  const canvas = document.createElement('canvas');
  canvas.width = sampleSize;
  canvas.height = sampleSize;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

  let data;
  try {
    data = ctx.getImageData(0, 0, sampleSize, sampleSize).data;
  } catch {
    return false;
  }

  let blankLike = 0;
  const total = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    const transparent = a < 20;
    const white = a >= 20 && r >= 242 && g >= 242 && b >= 242;
    const nearWhite = a >= 20 && r >= 225 && g >= 225 && b >= 225;
    const veryLight = a >= 20 && (r + g + b) / 3 >= 238;

    if (transparent || white || nearWhite || veryLight) {
      blankLike += 1;
    }
  }

  return blankLike / total >= 0.9;
};

const Login = () => {
  const { loginDestinazione } = useContext(DestinazioneContext);
  const { loading: globalLoading, apiToken, apiError, refreshToken, companyInfo } = useContext(LoginContext);
  const { darkMode } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [destlogin, setDestlogin] = useState("");
  const [destpsw, setDestpsw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  // Stati per recupero password
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  
  // Stato per il logo (azienda o fallback PanifyCloud)
  const [logoUrl, setLogoUrl] = useState(PANIFY_CLOUD_FALLBACK_LOGO);
  const [logoLoading, setLogoLoading] = useState(true);
  const [isPanifyFallbackLogo, setIsPanifyFallbackLogo] = useState(true);

  // Stato per mostrare/nascondere password
  const [showPassword, setShowPassword] = useState(false);

  const [showReleaseDetails, setShowReleaseDetails] = useState(false);

  // Funzione per toggle visibilità password
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Carica credenziali salvate se presenti (sopravvivono al logout di sessione)
  useEffect(() => {
    const saved = loadRememberedCredentials();
    if (saved) {
      setDestlogin(saved.email);
      setDestpsw(saved.password);
      setRememberMe(true);
    }
  }, []);

  const applyPanifyFallbackLogo = () => {
    setLogoUrl(PANIFY_CLOUD_FALLBACK_LOGO);
    setIsPanifyFallbackLogo(true);
  };

  const handleLoginLogoError = () => {
    if (!isPanifyFallbackLogo) {
      applyPanifyFallbackLogo();
    }
  };

  const handleLoginLogoLoad = (e) => {
    if (isPanifyFallbackLogo) return;
    const img = e.currentTarget;
    if (isMostlyBlankOrWhiteLogo(img)) {
      applyPanifyFallbackLogo();
    }
  };

  // Carica il logo aziendale; se assente o non valido → PanifyCloud (sfondo trasparente)
  useEffect(() => {
    const loadLogo = async () => {
      try {
        setLogoLoading(true);
        const logoBase64 = await configService.getLogo();
        const logoDataUrl = normalizeCompanyLogoDataUrl(logoBase64);
        if (logoDataUrl) {
          setLogoUrl(logoDataUrl);
          setIsPanifyFallbackLogo(false);
        } else {
          applyPanifyFallbackLogo();
        }
      } catch (error) {
        console.error("[Login] Errore nel caricamento del logo:", error);
        applyPanifyFallbackLogo();
      } finally {
        setLogoLoading(false);
      }
    };

    loadLogo();
  }, [apiToken]);

  // Verifica e riottieni il token se necessario
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      // Non ritentare se LoginContext ha già fallito: refreshToken() azzera apiError e rientra in loop infinito ("Rinnovo connessione...").
      if (!globalLoading && !apiToken && !isRefreshing && !apiError) {
        console.log("[Login] Token mancante, richiedo refresh...");
        setIsRefreshing(true);
        setError("");
        
        try {
          await refreshToken();
        } catch (err) {
          console.error("[Login] Errore durante il refresh del token:", err);
          setError("Errore di connessione al server. Ricarica la pagina.");
        } finally {
          setIsRefreshing(false);
        }
      }
    };
    
    checkAndRefreshToken();
  }, [globalLoading, apiToken, apiError, refreshToken, isRefreshing]);


  // Usa la cache condivisa: una sola GET /client-config
  const getClientsConfig = async () => {
    try {
      return await getClientConfig();
    } catch (error) {
      console.error('Config fallito:', error);
      return null;
    }
  };


  // Inizializza il cliente amministratore '0001' se non esiste
  useEffect(() => {
    const initializeAdminCliente = async () => {
      // Attendi che il token sia disponibile
      if (!apiToken) {
        return;
      }

      // Carica la configurazione
      const clientConfig = await getClientsConfig();
      

      try {
        console.log('🔍 === VERIFICA CLIENTE AMMINISTRATORE ===');
        
        // 1. Controlla se esiste il cliente con codice '0001'
        let clienteEsistente = null;
        try {
          const response = await clientiService.getClienti('0001');
          clienteEsistente = response;
          console.log('✅ Cliente 0001 già esistente:', clienteEsistente);
        } catch (error) {
            console.log('⚠️ Cliente 0001 non trovato, procedo con la creazione');
        }

        // 2. Se non esiste, crea il cliente amministratore
        if (!clienteEsistente) {
          console.log('🆕 === CREAZIONE CLIENTE AMMINISTRATORE ===');
          
          const nuovoCliente = {
            c_cliente: '0001',
            cliente: clientConfig.REACT_APP_AZIENDA_RAGIONE_SOCIALE || "Amministratore di sistema",
            indirizzo: clientConfig.REACT_APP_AZIENDA_INDIRIZZO || "non definito",
            citta: clientConfig.REACT_APP_AZIENDA_COMUNE || "non definita",
            prov: clientConfig.REACT_APP_AZIENDA_PROVINCIA || "RM",
            cap: clientConfig.REACT_APP_AZIENDA_CAP || "12345",
            piva: clientConfig.REACT_APP_AZIENDA_PARTITA_IVA || "00000000000",
            cod_fisc: clientConfig.REACT_APP_AZIENDA_CODICE_FISCALE || "",
            tel: clientConfig.REACT_APP_AZIENDA_NUMERO_TELEFONO || "",
            categ: "",
            categ2: "",
            condi_pag: "",
            email: "",
            persona: ""
          };

          const clienteCreato = await clientiService.createCliente(nuovoCliente);
          console.log('✅ Cliente amministratore creato:', clienteCreato);

          // 4. Aggiorna il configuratore con DEF_CLIENTE_INTERNO
          console.log('⚙️ === AGGIORNAMENTO CONFIGURATORE ===');
          
          // Costruisci l'oggetto per updateMultiConfigurator
          const configData = {
            "DEF_CLIE_INTERNO": "0001",
            "NOMEDIT": clientConfig.REACT_APP_AZIENDA_RAGIONE_SOCIALE,
            "DENOMINAZ1": clientConfig.REACT_APP_AZIENDA_RAGIONE_SOCIALE,
            "DENOMINAZ2": clientConfig.REACT_APP_AZIENDA_RAGIONE_SOCIALE,
            "DENOMINAZ3": clientConfig.REACT_APP_AZIENDA_RAGIONE_SOCIALE,
            "INDDIT": clientConfig.REACT_APP_AZIENDA_INDIRIZZO,
            "INDIRIZZO1": clientConfig.REACT_APP_AZIENDA_INDIRIZZO,
            "INDIRIZZO2": clientConfig.REACT_APP_AZIENDA_INDIRIZZO,
            "NAZIONE": "IT",
            "NAZIONE1": "IT",
            "NAZIONE2": "IT",
            "IDPAESE3": "IT",
            "PRODIT": clientConfig.REACT_APP_AZIENDA_PROVINCIA,
            "PROVINCIA1": clientConfig.REACT_APP_AZIENDA_PROVINCIA,
            "PROVINCIA2": clientConfig.REACT_APP_AZIENDA_PROVINCIA,
            "PIVADIT": clientConfig.REACT_APP_AZIENDA_PARTITA_IVA,
            "IDCODICE": clientConfig.REACT_APP_AZIENDA_PARTITA_IVA,
            "IDCODICE1": clientConfig.REACT_APP_AZIENDA_PARTITA_IVA,
            "IDCODICE3": clientConfig.REACT_APP_AZIENDA_PARTITA_IVA,
            "CODFISC": clientConfig.REACT_APP_AZIENDA_CODICE_FISCALE,
            "CODFISC1": clientConfig.REACT_APP_AZIENDA_CODICE_FISCALE,
            "CODFISC2": clientConfig.REACT_APP_AZIENDA_CODICE_FISCALE,
            "CODFISC3": clientConfig.REACT_APP_AZIENDA_CODICE_FISCALE,
            "CITDIT": clientConfig.REACT_APP_AZIENDA_COMUNE ,
            "COMUNE1": clientConfig.REACT_APP_AZIENDA_COMUNE ,
            "COMUNE2": clientConfig.REACT_APP_AZIENDA_COMUNE ,
            "CAPDIT": clientConfig.REACT_APP_AZIENDA_CAP,
            "CAP1": clientConfig.REACT_APP_AZIENDA_CAP,
            "CAP2": clientConfig.REACT_APP_AZIENDA_CAP,
            "TELDIT": clientConfig.REACT_APP_AZIENDA_NUMERO_TELEFONO,
            "TEL1": clientConfig.REACT_APP_AZIENDA_NUMERO_TELEFONO,
            "EMAIL_DITTA": clientConfig.REACT_APP_API_EMAIL
          };
          await configService.updateMultiConfigurator(configData);

          // await configService.updateConfigurator('DEF_CLIE_INTERNO', '0001');
          console.log('🎉 === INIZIALIZZAZIONE COMPLETATA ===');
        }

        // 5. Verifica / crea la destinazione amministratore (cliente 0001, codice destinazione 0001).
        //    primario atteso = macli + def_clie_interno (configurator); codicede atteso = 0001.
        console.log('📝 === VERIFICA DESTINAZIONE AMMINISTRATORE (0001) ===');
        const datiDestinazione = {
          nomede: clientConfig.REACT_APP_AZIENDA_RAGIONE_SOCIALE || "Amministratore di sistema",
          viade: clientConfig.REACT_APP_AZIENDA_INDIRIZZO || "non definito",
          cittade: clientConfig.REACT_APP_AZIENDA_COMUNE || "non definita",
          provinciade: clientConfig.REACT_APP_AZIENDA_PROVINCIA || "RM",
          nazionede: "IT",
          capde: clientConfig.REACT_APP_AZIENDA_CAP || "12345",
          destlogin: clientConfig.REACT_APP_API_EMAIL || "",
          destpsw: clientConfig.REACT_APP_API_PASSWORD || "",
          orderactive: "1",
          lun_act: "0",
          mar_act: "0",
          mer_act: "0",
          gio_act: "0",
          ven_act: "0",
          sab_act: "0",
          dom_act: "0",
          spec_act: "0"
        };

        const adminCodiceDest = '0001';
        const isEmptyField = (v) =>
          v === undefined || v === null || String(v).trim() === '';

        let macliStr = '';
        try {

          const macliVal = await configService.getMastroCclienti();
          macliStr = macliVal != null ? String(macliVal).trim() : '';
          // In errore getMastroCclienti() restituisce il placeholder "Azienda" (configService)
          if (macliStr === 'Azienda') macliStr = '';
        } catch (e) {
          console.warn('[Init admin dest] macli non disponibile:', e?.message);
        }

        let defClieStr = '0001';
        try {
          const defVal = await configService.getDefaultClienteInterno();
          if (defVal != null && String(defVal).trim() !== '') {
            defClieStr = String(defVal).trim();
          }
        } catch (e) {
          console.warn('[Init admin dest] def_clie_interno non disponibile, uso 0001:', e?.message);
        }

        const expectedPrimario = `${macliStr}${defClieStr}`;
        console.log(
          '[Init admin dest] macli:',
          macliStr || '(vuoto)',
          '+ def_clie_interno:',
          defClieStr,
          '→ primario atteso:',
          expectedPrimario,
          '| codicede atteso:',
          adminCodiceDest
        );

        const maxAttempts = 3;
        let destinazioneOk = false;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          const cliente0001 = await clientiService.getClienti('0001');
          const destinazioni = cliente0001?.destinazioni_clie || [];

          const destinazioneAdmin = destinazioni.find((dest) => {
            const primario = isEmptyField(dest?.primario)
              ? expectedPrimario
              : String(dest.primario).trim();
            const codicede = isEmptyField(dest?.codicede)
              ? adminCodiceDest
              : String(dest.codicede).trim();
            return primario === expectedPrimario && codicede === adminCodiceDest;
          });

          const hasDestLogin = !!String(destinazioneAdmin?.destlogin ?? '').trim();
          const hasDestPsw = !!String(destinazioneAdmin?.destpsw ?? '').trim();
          const isOrderActive = String(destinazioneAdmin?.orderactive ?? '') === '1';

          if (destinazioneAdmin && hasDestLogin && hasDestPsw && isOrderActive) {
            destinazioneOk = true;
            console.log(`✅ Destinazione admin valida (tentativo ${attempt}/${maxAttempts})`);
            break;
          }

          console.log(
            `⚠️ Destinazione admin (primario=${expectedPrimario}, codicede=${adminCodiceDest}) assente o incompleta (tentativo ${attempt}/${maxAttempts}), eseguo salvaDestinazione...`
          );
          await clientiService.salvaDestinazione('0001', adminCodiceDest, datiDestinazione);
        }

        if (!destinazioneOk) {
          console.error(
            '❌ Destinazione admin non valida dopo i tentativi (primario atteso:',
            expectedPrimario,
            ')'
          );
        } else {
          console.log('✅ Verifica destinazione admin completata con successo');
        }

      } catch (error) {
        console.error('❌ Errore durante l\'inizializzazione del cliente amministratore:', error);
        // Non mostriamo errori all'utente per non bloccare il login
        // L'inizializzazione può essere ritentata al prossimo caricamento
      }
    };

    initializeAdminCliente();
  }, [apiToken]); // Esegui solo quando il token è disponibile

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Verifica che abbiamo il token API prima di procedere
    if (!apiToken) {
      setError("Sistema non pronto. Attendi qualche secondo e riprova.");
      return;
    }
    
    setLoading(true);

    // Piccolo delay per assicurare che tutto sia pronto
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const result = await loginDestinazione(destlogin, destpsw, navigate);
      
      if (result && result.success) {
        // Gestisci "Ricordami": unico punto in cui si dimenticano le credenziali
        if (rememberMe) {
          persistRememberedCredentials(destlogin, destpsw);
        } else {
          forgetRememberedCredentials();
        }
        
        // Se non è stata fatta la navigazione alle destinazioni, naviga alla home
        if (!result.navigatedToDestinazioni) {
          navigate("/home");
        }
      } else {
        setError("Login fallito. Controlla le credenziali.");
      }
    } catch (err) {
      console.error("[Login] Errore durante il login:", err);
      setError("Errore durante il login. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  // Funzione per gestire il recupero password
  const handlePasswordRecovery = async () => {
    console.log('🔍 Inizio procedura di recupero');

    if (!recoveryEmail) {
      setRecoveryError("Inserisci un'email valida");
      return;
    }

    setRecoveryLoading(true);
    setRecoveryError("");
    setRecoveryMessage("");

    try {
      console.log('🔍 Verifica esistenza utente:', recoveryEmail);
      
      // 1. Verifica se l'email esiste nelle destinazioni
      const destinazioni = await clientiService.getDestinazioniByEmail(recoveryEmail);
      
      console.log('📊 Destinazioni trovate:', destinazioni);
      
      // 2. Se l'array è vuoto, l'utente non è registrato
      if (!destinazioni || destinazioni.length === 0) {
        setRecoveryError("❌ Utente non registrato");
        return;
      }
      
      // 3. Recupera i dati della prima destinazione trovata
      const destinazione = destinazioni[0];
      
      // 4. Costruisci il body per l'invio credenziali
      const datiCredenziali = {
        ragionesociale: destinazione.nomede,
        nomedestinazione: destinazione.nomede,
        username: destinazione.destlogin || recoveryEmail,
        password: destinazione.destpsw || '',
        urlaccesso: window.location.origin // URL base dell'applicazione
      };
      
      console.log('📧 Invio credenziali:', datiCredenziali);
      
      // 5. Invia le credenziali via email
      await orderService.invioCredenzialiCliente(datiCredenziali);
      
      setRecoveryMessage("✅ Credenziali inviate via email con successo!");
      
      // Chiudi il modal dopo 3 secondi
      setTimeout(() => {
        setShowRecoveryModal(false);
        setRecoveryEmail("");
        setRecoveryMessage("");
      }, 3000);
      
    } catch (error) {
      console.error("💥 Errore nel recupero password:", error);
      setRecoveryError(
        error.message || "Errore durante il recupero password. Riprova."
      );
    } finally {
      setRecoveryLoading(false);
    }
  };

  // Funzione per aprire il modal di recupero password
  const openRecoveryModal = () => {
    setRecoveryEmail(destlogin); // Pre-compila con l'email inserita nel login
    setRecoveryError("");
    setRecoveryMessage("");
    setShowRecoveryModal(true);
  };
  

  // Se stiamo caricando il token iniziale o facendo refresh
  if (globalLoading || isRefreshing) {
    return (
      <Container className={`d-flex justify-content-center align-items-center ${darkMode ? "text-light" : ""}`} style={{ minHeight: "100vh", backgroundColor: darkMode ? "#121212" : "#FFFEF9" }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3" variant={darkMode ? "light" : "primary"}>
            <span className="visually-hidden">Caricamento...</span>
          </Spinner>
          <p className="mt-3">
            {isRefreshing ? "Rinnovo connessione al server..." : "Connessione al server in corso..."}
          </p>
        </div>
      </Container>
    );
  }

  // Se c'è un errore API globale e non abbiamo token
  if (apiError && !apiToken) {
    return (
      <Container className={`d-flex justify-content-center align-items-center ${darkMode ? "text-light" : ""}`} style={{ minHeight: "100vh", backgroundColor: darkMode ? "#121212" : "#FFFEF9" }}>
        <Alert variant="danger" className="text-center">
          <h4>Errore di connessione</h4>
          <p>{apiError}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Ricarica la pagina
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className={`d-flex flex-column justify-content-center align-items-center ${darkMode ? "text-light" : ""}`} style={{ minHeight: "100vh", backgroundColor: darkMode ? "#121212" : "#FFFEF9" }}>
      <Form onSubmit={handleSubmit} className={`login-form p-4 shadow rounded ${darkMode ? "text-light border border-secondary" : ""}`} style={{ maxWidth: "400px", width: "100%", background: darkMode ? "#2d2a24" : "linear-gradient(to bottom, #FAF2A1 0%, #ffffff 100%)" }}>
        <h2 className="text-center mb-4 login-heading">Accedi a PanifyCloud</h2>

        {/* Logo aziendale o fallback PanifyCloud */}
        <div className="text-center mb-4" style={{ minHeight: logoLoading ? '72px' : undefined }}>
          {logoLoading ? (
            <Spinner animation="border" size="sm" variant={darkMode ? 'light' : 'primary'} />
          ) : (
            <img
              src={logoUrl}
              alt={isPanifyFallbackLogo ? 'PanifyCloud' : 'Logo azienda'}
              onError={handleLoginLogoError}
              onLoad={handleLoginLogoLoad}
              style={{
                maxWidth: isPanifyFallbackLogo ? '240px' : '200px',
                maxHeight: '100px',
                objectFit: 'contain',
              }}
              className="img-fluid"
            />
          )}
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        
        {/* Messaggio sui giorni di sospensione ordini (solo se date reali dd/mm/yyyy da API) */}
        {companyInfo?.giornoChiusura &&
          companyInfo?.giornoApertura &&
          isValidItalianDate(companyInfo.giornoChiusura) &&
          isValidItalianDate(companyInfo.giornoApertura) &&
          (() => {
          const getItalianDate = () => {
            const date = new Date();
            const options = { timeZone: 'Europe/Rome' };
            const italianDateString = date.toLocaleString('en-US', options);
            return new Date(italianDateString);
          };
          const oggi = getItalianDate();
         
          const dataChiusura = parseItalianDate(companyInfo.giornoChiusura);
          const dataApertura = parseItalianDate(companyInfo.giornoApertura);
          if (!dataChiusura || !dataApertura) return false;
      
          // Calcola 10 giorni prima della chiusura
          const dataInizioAvviso = new Date(dataChiusura);
          dataInizioAvviso.setDate(dataInizioAvviso.getDate() - 10);
          
          // Verifica se oggi è compreso tra (chiusura - 10 giorni) e apertura
          const isInPeriodoAvviso = oggi >= dataInizioAvviso && oggi <= dataApertura;

          return isInPeriodoAvviso;
        })() && (
          <Alert variant="warning" className="mb-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            La fornitura sarà sospesa dalla data: <strong>{companyInfo.giornoChiusura}</strong> alla data: <strong>{companyInfo.giornoApertura}</strong>
          </Alert>
        )}
        
        {/* Mostra un avviso se il token non è pronto */}
        {!apiToken && (
          <Alert variant="warning">
            Connessione al server in corso...
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            className="login-field"
            placeholder="Inserisci email"
            value={destlogin}
            onChange={(e) => setDestlogin(e.target.value)}
            required
            disabled={!apiToken || loading}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Password</Form.Label>
          <div className="position-relative">
            <Form.Control
              type={showPassword ? "text" : "password"}
              className="login-field"
              placeholder="Inserisci password"
              value={destpsw}
              onChange={(e) => setDestpsw(e.target.value)}
              required
              disabled={!apiToken || loading}
              style={{ paddingRight: '45px' }}
            />
            <Button
              variant="link"
              className="login-password-toggle"
              onClick={togglePasswordVisibility}
              disabled={!apiToken || loading}
              title={showPassword ? "Nascondi password" : "Mostra password"}
              aria-label={showPassword ? "Nascondi password" : "Mostra password"}
              style={{
                position: 'absolute',
                right: '4px',
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'transparent',
                color: darkMode ? '#adb5bd' : '#6c757d',
                zIndex: 10
              }}
            >
              <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </Button>
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check 
            type="checkbox"
            id="rememberMe"
            label="Ricordami"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            disabled={!apiToken || loading}
          />
        </Form.Group>

        <Button 
          type="submit" 
          variant="primary" 
          className="w-100 login-submit" 
          disabled={loading || !apiToken}
        >
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
              Accesso in corso...
            </>
          ) : (
            "Accedi"
          )}
        </Button>

        {/* Link per recupero password - per ora disattivato fin quando non creo il modulo */}

        <div className="text-center mt-3">
          <Button 
            variant="link" 
            size="sm" 
            onClick={openRecoveryModal}
            disabled={!apiToken}
            className={darkMode ? "text-info p-0" : "p-0"}
          >
            Hai dimenticato la password?
          </Button>
        </div>

      </Form>
      
      <div className={`mt-3 text-center ${darkMode ? "text-light" : ""}`}>
        <span>Non hai un account? </span>
        <Button variant="link" onClick={() => navigate("/registrazione")} className={darkMode ? "text-info" : ""}>Registrati</Button>
      </div>

      {(() => {
        const current = getCurrentRelease();
        if (!current) return null;
        const firstLine = current.items?.[0] ?? '';
        const preview =
          firstLine.length > 120 ? `${firstLine.slice(0, 120)}…` : firstLine;
        return (
          <div
            className={`mt-4 pt-3 border-top w-100 ${darkMode ? 'border-secondary' : ''}`}
            style={{ maxWidth: '400px' }}
          >
            <div
              className={`small text-center mb-2 ${darkMode ? 'text-white-50' : 'text-muted'}`}
            >
              Versione <strong className={darkMode ? 'text-white' : ''}>{current.version}</strong>
              {' · '}
              Deploy {formatDeployDateIt(current.deployDate)}
            </div>
            <div
              className={`shadow rounded overflow-hidden border ${darkMode ? 'border-secondary' : ''}`}
              style={{
                background: darkMode
                  ? '#2d2a24'
                  : 'linear-gradient(to bottom, #FAF2A1 0%, #ffffff 100%)',
              }}
            >
              <div
                className={`py-2 px-3 ${darkMode ? 'text-light' : 'text-dark'}`}
                role="button"
                tabIndex={0}
                onClick={() => setShowReleaseDetails((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowReleaseDetails((v) => !v);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div className="text-start">
                    <strong className={`d-block ${darkMode ? 'text-white' : ''}`}>
                      Novità in questa versione
                    </strong>
                    <span className={`small ${darkMode ? 'text-white-50' : 'text-muted'}`}>
                      {current.items?.length
                        ? `${current.items.length} aggiornamenti — ${preview}`
                        : 'Dettagli release'}
                    </span>
                  </div>
                  <i
                    className={`fas fa-chevron-${showReleaseDetails ? 'up' : 'down'} mt-1 ${
                      darkMode ? 'text-white-50' : 'text-muted'
                    }`}
                    aria-hidden
                  />
                </div>
              </div>
              <Collapse in={showReleaseDetails}>
                <div
                  className={`small p-3 border-top ${
                    darkMode ? 'text-light border-secondary' : 'text-dark'
                  }`}
                >
                  <p className={`fw-semibold mb-2 ${darkMode ? 'text-white' : ''}`}>
                    {current.title || `Versione ${current.version}`} ({formatDeployDateIt(current.deployDate)})
                  </p>
                  <ul className="mb-4 ps-3">
                    {(current.items || []).map((line, i) => (
                      <li key={`cur-${i}`} className="mb-1">
                        {line}
                      </li>
                    ))}
                  </ul>
                  {RELEASE_HISTORY.slice(1).map((rel) => (
                    <div key={rel.version} className="mb-3">
                      <hr className={darkMode ? 'border-secondary opacity-50' : ''} />
                      <p className={`fw-semibold mb-2 ${darkMode ? 'text-white-50' : 'text-muted'}`}>
                        Versione {rel.version} — Deploy {formatDeployDateIt(rel.deployDate)}
                      </p>
                      <ul className="mb-0 ps-3">
                        {(rel.items || []).map((line, i) => (
                          <li key={`${rel.version}-${i}`} className="mb-1">
                            {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Collapse>
            </div>
          </div>
        );
      })()}

      {/* Modal per recupero password */}
      <Modal 
        show={showRecoveryModal} 
        onHide={() => setShowRecoveryModal(false)} 
        centered
        data-bs-theme={darkMode ? "dark" : "light"}
      >
        <Modal.Header 
          closeButton 
          className={darkMode ? "text-light border-secondary" : ""}
          style={darkMode ? { backgroundColor: '#2d2a24' } : undefined}
        >
          <Modal.Title>Recupero Password</Modal.Title>
        </Modal.Header>
        <Modal.Body className={darkMode ? "text-light border-secondary" : ""} style={darkMode ? { backgroundColor: '#2d2a24' } : undefined}>
          {recoveryMessage && (
            <Alert variant="success">{recoveryMessage}</Alert>
          )}
          {recoveryError && (
            <Alert variant="danger">{recoveryError}</Alert>
          )}
          
          <Form className="login-form">
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Inserisci la tua email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                disabled={recoveryLoading}
              />
              <Form.Text className={darkMode ? "text-white-50" : "text-muted"}>
                Inserisci l'email associata al tuo account. Ti invieremo la password.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className={darkMode ? "border-secondary" : ""} style={darkMode ? { backgroundColor: '#2d2a24' } : undefined}>
          <Button 
            variant="secondary" 
            onClick={() => setShowRecoveryModal(false)}
            disabled={recoveryLoading}
          >
            Annulla
          </Button>
          <Button 
            variant="primary" 
            onClick={handlePasswordRecovery}
            disabled={recoveryLoading || !recoveryEmail}
          >
            {recoveryLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                Invio in corso...
              </>
            ) : (
              "Invia Nuova Password"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Login;